import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { axiosInstance, API } from "../../utils/api";

const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [token, setToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenParam = params.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      setTokenValid(true);
    }
    setLoading(false);
  }, [location]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters');
      return;
    }

    try {
      await axiosInstance.post(`${API}/auth/reset-password`, {
        token,
        new_password: newPassword
      });
      setSuccess(true);
      toast.success('Password reset successful! You can now login.');
      setTimeout(() => navigate('/'), 3000);
    } catch (error) {
      setError(error.response?.data?.detail || 'Failed to reset password. The link may have expired.');
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  if (loading) {
    return (
      <div className="app-container">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  return (
    <div className="app-container">
      <Toaster position="top-right" richColors />
      <div className="auth-page">
        <div className="auth-card reset-password-card">
          <div className="reset-password-logo">
            <img src="/cxrecon-logo-new.png" alt="CXrecon" className="reset-logo-img" />
          </div>
          <h1>Reset Your Password</h1>
          
          {!tokenValid ? (
            <div className="reset-invalid-token">
              <div className="invalid-token-icon">⚠️</div>
              <h2>Invalid Reset Link</h2>
              <p>This password reset link is invalid or has expired.</p>
              <p>Please request a new password reset link from the login page.</p>
              <button 
                className="auth-submit-btn"
                onClick={() => navigate('/')}
              >
                Go to Login
              </button>
            </div>
          ) : !success ? (
            <>
              <p className="reset-instructions">
                Enter your new password below. Make sure it's at least 6 characters long.
              </p>
              <form onSubmit={handleResetPassword} className="auth-form">
                <div className="form-group">
                  <label className="form-label">New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength="6"
                    placeholder="Enter new password"
                    autoFocus
                    data-testid="new-password-input"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Confirm New Password</label>
                  <input
                    type="password"
                    className="form-input"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    minLength="6"
                    placeholder="Confirm new password"
                    data-testid="confirm-password-input"
                  />
                </div>

                {error && (
                  <div className="auth-error" data-testid="reset-error">
                    {error}
                  </div>
                )}

                <button 
                  type="submit" 
                  className="auth-submit-btn"
                  data-testid="reset-password-submit"
                >
                  Reset Password
                </button>
              </form>
            </>
          ) : (
            <div className="reset-success">
              <div className="success-icon">✅</div>
              <h2>Password Reset Successful!</h2>
              <p>Your password has been updated. You can now sign in with your new password.</p>
              <p className="redirect-message">Redirecting to login...</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
